import { useState, useEffect } from 'react';
import { AppContent, NavigationConfig } from '../types';
import { IContentService, createContentService } from '../services/ContentService';
import { IThemeService, createThemeService } from '../services/ThemeService';

export interface AppViewModelState {
  content: AppContent | null;
  navigation: NavigationConfig | null;
  isLoading: boolean;
  error: string | null;
  themeClasses: any;
}

export interface AppViewModelActions {
  loadContent: () => Promise<void>;
  refreshContent: () => Promise<void>;
  clearError: () => void;
}

export type AppViewModel = AppViewModelState & AppViewModelActions;

export const useAppViewModel = (): AppViewModel => {
  const [state, setState] = useState<AppViewModelState>({
    content: null,
    navigation: null,
    isLoading: true,
    error: null,
    themeClasses: null
  });

  // Injeção de dependência dos services
  const contentService: IContentService = createContentService();
  const themeService: IThemeService = createThemeService();

  const loadContent = async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const [content, navigation, themeClasses] = await Promise.all([
        contentService.getAppContent(),
        contentService.getNavigationConfig(),
        themeService.getThemeClasses()
      ]);

      setState(prev => ({
        ...prev,
        content,
        navigation,
        themeClasses,
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro ao carregar conteúdo',
        isLoading: false
      }));
    }
  };

  const refreshContent = async (): Promise<void> => {
    // Limpa cache e recarrega
    contentService.clearCache?.();
    await loadContent();
  };

  const clearError = (): void => {
    setState(prev => ({ ...prev, error: null }));
  };

  // Carrega conteúdo na inicialização
  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...state,
    loadContent,
    refreshContent,
    clearError
  };
};